package com.sweetlime.bingeamigo.presentation.features.user

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.SegmentedButtonDefaults.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.bingeamigo.model.Episode
import com.sweetlime.bingeamigo.model.Movie
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.presentation.components.MovieCard
import com.sweetlime.bingeamigo.presentation.components.UserActivityCard
import com.sweetlime.bingeamigo.presentation.features.moviedetail.MovieDetailScreen
import com.sweetlime.bingeamigo.presentation.features.tvdetail.EpisodeDetailScreen
import com.sweetlime.bingeamigo.presentation.features.tvdetail.TvShowDetailScreen
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.service.XpService
import org.koin.compose.koinInject

@Composable
fun UserProfileContent(
    userId: String,
    viewModel: UserProfileViewModel = koinInject(),
    onBackClick: () -> Unit
) {

    val uiState by viewModel.uiState.collectAsState()
    val communityRatings by viewModel.communityRatings.collectAsState()

    val navigator = LocalNavigator.currentOrThrow

    LaunchedEffect(Unit) {
        viewModel.loadUser(userId)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {

        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = 120.dp)
        ) {

            // 👤 HEADER
            item {

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(
                            Brush.verticalGradient(
                                colors = listOf(
                                    BrandColors.Secondary,
                                    BrandColors.Background
                                )
                            )
                        )
                ) {

                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 88.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {

                        val username =
                            uiState.profile?.username
                                ?: "Movie Lover"

                        val level =
                            uiState.profile?.level ?: 1

                        val xp =
                            uiState.profile?.xp ?: 0

                        // Avatar
                        Box(
                            modifier = Modifier
                                .size(110.dp)
                                .clip(CircleShape)
                                .background(
                                    Color.Black.copy(alpha = 0.25f)
                                ),
                            contentAlignment = Alignment.Center
                        ) {

                            Text(
                                text = username
                                    .take(1)
                                    .uppercase(),
                                color = Color.White,
                                fontSize = 42.sp,
                                fontWeight = FontWeight.Bold
                            )
                        }

                        Spacer(modifier = Modifier.height(20.dp))

                        Text(
                            text = username,
                            color = Color.White,
                            style = MaterialTheme.typography.headlineSmall,
                            fontWeight = FontWeight.Bold
                        )

                        Spacer(modifier = Modifier.height(16.dp))

                        val levelTitle = XpService.levelName(level)
                        val nextLevelXp = XpService.xpRequired(level + 1)
                        val currentLevelXp = XpService.xpRequired(level)
                        val xpNeededForLevel = (nextLevelXp - currentLevelXp).coerceAtLeast(1)
                        val progress = ((xp - currentLevelXp).toFloat() / xpNeededForLevel.toFloat()).coerceIn(0f, 1f)

                        Card(
                            colors = CardDefaults.cardColors(
                                containerColor = Color.Black.copy(alpha = 0.25f)
                            ),
                            shape = RoundedCornerShape(20.dp)
                        ) {
                            Column(
                                modifier = Modifier.padding(
                                    horizontal = 24.dp,
                                    vertical = 10.dp
                                ),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Text(
                                    text = "Level $level",
                                    color = Color.White,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    text = levelTitle,
                                    color = BrandColors.Secondary,
                                    style = MaterialTheme.typography.bodySmall,
                                    fontWeight = FontWeight.Medium
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                LinearProgressIndicator(
                                    progress = { progress },
                                    modifier = Modifier.width(180.dp),
                                    color = BrandColors.Secondary,
                                    trackColor = Color.White.copy(alpha = 0.2f)
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = "$xp / $nextLevelXp XP",
                                    color = Color.White.copy(alpha = 0.8f),
                                    style = MaterialTheme.typography.bodySmall
                                )
                            }
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        if (uiState.isFollowing) {
                            OutlinedButton(
                                onClick = { viewModel.unfollowUser() },
                                border = BorderStroke(1.dp, Color.White.copy(alpha = 0.6f)),
                                shape = RoundedCornerShape(16.dp)
                            ) {
                                Text(text = "Following", color = Color.White)
                            }
                        } else {
                            Button(
                                onClick = { viewModel.followUser() },
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = BrandColors.Secondary
                                ),
                                shape = RoundedCornerShape(16.dp)
                            ) {
                                Text(
                                    text = "Follow",
                                    color = Color.White,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }

                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }
            }

            // 👥 FOLLOWERS / FOLLOWING
            item {
                Spacer(modifier = Modifier.height(16.dp))
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 20.dp)
                        .background(BrandColors.Surface, RoundedCornerShape(20.dp))
                        .padding(horizontal = 24.dp, vertical = 14.dp),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Row(horizontalArrangement = Arrangement.spacedBy(40.dp)) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = uiState.followersCount.toString(),
                                color = Color.White,
                                fontWeight = FontWeight.Bold,
                                fontSize = 20.sp
                            )
                            Text("Followers", color = Color.White.copy(alpha = 0.75f))
                        }
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = uiState.followingCount.toString(),
                                color = Color.White,
                                fontWeight = FontWeight.Bold,
                                fontSize = 20.sp
                            )
                            Text("Following", color = Color.White.copy(alpha = 0.75f))
                        }
                    }
                }
            }

            // 🎬 FAVORITES
            item {

                Spacer(modifier = Modifier.height(28.dp))

                Text(
                    text = "Favorites",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 20.dp)
                )

                Spacer(modifier = Modifier.height(16.dp))
            }

            item {

                if (uiState.favorites.isEmpty()) {

                    Text(
                        text = "No favorites yet",
                        color = Color.Gray,
                        modifier = Modifier.padding(horizontal = 20.dp)
                    )

                } else {

                    LazyRow(
                        contentPadding = PaddingValues(horizontal = 20.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(uiState.favorites.take(10)) { movie ->
                            MovieCard(
                                libraryMovie = movie,
                                modifier = Modifier.width(120.dp),
                                communityRating = communityRatings[movie.id]
                            )
                        }
                    }
                }
            }

            // 📢 ACTIVITY
            item {

                Spacer(modifier = Modifier.height(32.dp))

                Text(
                    text = "Recent Activity",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 20.dp)
                )

                Spacer(modifier = Modifier.height(16.dp))
            }

            if (uiState.activity.isEmpty()) {

                item {

                    Text(
                        text = "No recent activity",
                        color = Color.Gray,
                        modifier = Modifier.padding(horizontal = 20.dp)
                    )
                }

            } else {

                items(uiState.activity) { activity ->

                    UserActivityCard(activity, showUsername = false, onClick = { a ->
                        val tvShow = if (a.tvShowId != 0) TvShow(id = a.tvShowId, name = a.tvShowTitle, overview = "", posterPath = a.tvShowPoster) else null
                        when {
                            a.episodeId != 0 && tvShow != null -> navigator.push(EpisodeDetailScreen(
                                tvShow = tvShow,
                                episode = Episode(id = a.episodeId, name = a.episodeName, episodeNumber = a.episodeNumber, seasonNumber = a.seasonNumber, stillPath = a.episodeStillPath.ifEmpty { null }),
                                totalEpisodesInShow = 0
                            ))
                            tvShow != null -> navigator.push(TvShowDetailScreen(tvShow))
                            else -> navigator.push(MovieDetailScreen(Movie(id = a.movieId, title = a.movieTitle, overview = "", posterPath = a.moviePoster)))
                        }
                    })
                }
            }
        }

        // 🔙 Back Button
        Box(
            modifier = Modifier
                .statusBarsPadding()
                .padding(
                    start = 20.dp,
                    top = 12.dp
                )
                .size(44.dp)
                .clip(CircleShape)
                .background(
                    Color.Black.copy(alpha = 0.45f)
                )
                .clickable {
                    onBackClick()
                },
            contentAlignment = Alignment.Center
        ) {

            Icon(
                imageVector = Icons.Default.ArrowBack,
                contentDescription = null,
                tint = Color.White
            )
        }
    }
}